import { useEffect, useState } from 'react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useAdjustCustomerBalanceMutation, useCustomerQuery, useCustomerStatementQuery,
  useDeleteCustomerMutation, useUpdateCustomerMutation,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney } from '@/lib/format';
import {
  Alert, Badge, Button, ConfirmDialog, ErrorState, Input, LoadingState, Modal, Pagination,
  Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';

const ENTRY_TONE: Record<string, 'neutral' | 'success' | 'warning' | 'danger' | 'brand'> = {
  OPENING: 'neutral',
  INVOICE: 'warning',
  PAYMENT: 'success',
  CREDIT_NOTE: 'brand',
  REFUND: 'danger',
  ADJUSTMENT: 'neutral',
};

/**
 * A customer's account: what they owe and every movement behind it.
 *
 * The statement is the explanation of the balance, so it is shown here rather
 * than on a separate screen — a figure nobody can account for is the failure
 * this module exists to prevent.
 */
export function CustomerDetailModal({
  customerId,
  onClose,
}: {
  customerId: string;
  onClose: () => void;
}) {
  const format = useFormat();
  const [page, setPage] = useState(1);
  const [adjusting, setAdjusting] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const { data: customer, isLoading, isError, error, refetch } = useCustomerQuery(customerId);
  const statement = useCustomerStatementQuery({ id: customerId, params: { page, limit: 15 } });
  const [adjustBalance, adjustState] = useAdjustCustomerBalanceMutation();
  const [updateCustomer, updateState] = useUpdateCustomerMutation();
  const [deleteCustomer] = useDeleteCustomerMutation();

  const [details, setDetails] = useState<Record<string, string> | null>(null);
  const [editing, setEditing] = useState(false);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (!customer) return;
    setDetails({
      name: customer.name,
      phone: customer.phone ?? '',
      email: customer.email ?? '',
      city: customer.city ?? '',
      creditLimit: customer.creditLimit,
      paymentTermDays: String(customer.paymentTermDays),
    });
  }, [customer]);

  const saveDetails = async (): Promise<void> => {
    if (!details) return;
    setFailure(null);
    try {
      await updateCustomer({
        id: customerId,
        body: {
          name: details.name,
          phone: details.phone || undefined,
          email: details.email || undefined,
          city: details.city || undefined,
          creditLimit: Number(details.creditLimit),
          paymentTermDays: Number(details.paymentTermDays),
        },
      }).unwrap();
      setEditing(false);
    } catch (saveError) {
      // Credit fields need CUSTOMER_CREDIT_MANAGE; the API says so plainly.
      setFailure(apiErrorMessage(saveError, 'Those changes could not be saved'));
    }
  };

  const confirmArchive = async (): Promise<void> => {
    setFailure(null);
    try {
      await deleteCustomer(customerId).unwrap();
      onClose();
    } catch (archiveError) {
      // Refused while a balance is outstanding.
      setFailure(apiErrorMessage(archiveError, 'That customer could not be archived'));
      setArchiving(false);
    }
  };

  const submitAdjustment = async (): Promise<void> => {
    setFailure(null);
    try {
      await adjustBalance({ id: customerId, amount: Number(amount), reason }).unwrap();
      setAmount('');
      setReason('');
      setAdjusting(false);
    } catch (adjustError) {
      setFailure(apiErrorMessage(adjustError, 'That adjustment could not be posted'));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={customer ? customer.name : 'Customer'}
      description={customer ? customer.code : undefined}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {!editing && (
            <>
              <Can permission={P.CUSTOMER_DELETE}>
                <Button
                  variant="outline"
                  onClick={() => {
                    setFailure(null);
                    setArchiving(true);
                  }}
                >
                  Archive
                </Button>
              </Can>
              <Can permission={P.CUSTOMER_UPDATE}>
                <Button onClick={() => setEditing(true)}>Edit</Button>
              </Can>
            </>
          )}
          {editing && (
            <>
              <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
              <Button
                loading={updateState.isLoading}
                disabled={!details?.name}
                onClick={() => void saveDetails()}
              >
                Save changes
              </Button>
            </>
          )}
        </>
      }
    >
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
      ) : customer ? (
        <div className="space-y-5">
          {failure && <Alert tone="danger">{failure}</Alert>}

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-subtle p-3">
              <p className="text-xs text-muted">Balance</p>
              <p
                className={`text-lg font-semibold ${
                  Number(customer.balance) > 0 ? 'text-red-600' : ''
                }`}
              >
                {formatMoney(customer.balance, format)}
              </p>
            </div>
            <div className="rounded-lg border border-subtle p-3">
              <p className="text-xs text-muted">Credit limit</p>
              <p className="text-lg font-semibold">{formatMoney(customer.creditLimit, format)}</p>
            </div>
            <div className="rounded-lg border border-subtle p-3">
              <p className="text-xs text-muted">Credit left</p>
              <p className="text-lg font-semibold">
                {customer.availableCredit === null
                  ? 'Cash only'
                  : formatMoney(customer.availableCredit, format)}
              </p>
            </div>
          </div>

          {customer.isOverLimit && (
            <Alert tone="warning">
              This customer is at or over their credit limit. New credit sales will be refused
              until the balance comes down.
            </Alert>
          )}

          {editing && details && (
            <div className="space-y-3 rounded-lg border border-subtle bg-sunken p-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  label="Name"
                  value={details.name}
                  onChange={(event) => setDetails({ ...details, name: event.target.value })}
                />
                <Input
                  label="Phone"
                  value={details.phone}
                  onChange={(event) => setDetails({ ...details, phone: event.target.value })}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  label="Email"
                  type="email"
                  value={details.email}
                  onChange={(event) => setDetails({ ...details, email: event.target.value })}
                />
                <Input
                  label="City"
                  value={details.city}
                  onChange={(event) => setDetails({ ...details, city: event.target.value })}
                />
              </div>
              <Can
                permission={P.CUSTOMER_CREDIT_MANAGE}
                fallback={
                  <p className="text-xs text-muted">
                    Credit limits and payment terms need the credit-management permission.
                  </p>
                }
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    label="Credit limit"
                    type="number"
                    min="0"
                    step="0.01"
                    value={details.creditLimit}
                    onChange={(event) =>
                      setDetails({ ...details, creditLimit: event.target.value })
                    }
                  />
                  <Input
                    label="Payment terms (days)"
                    type="number"
                    min="0"
                    value={details.paymentTermDays}
                    onChange={(event) =>
                      setDetails({ ...details, paymentTermDays: event.target.value })
                    }
                  />
                </div>
              </Can>
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium">Statement</h3>
            <Can permission={P.CUSTOMER_CREDIT_MANAGE}>
              {!adjusting && (
                <Button size="sm" variant="outline" onClick={() => setAdjusting(true)}>
                  Adjust balance
                </Button>
              )}
            </Can>
          </div>

          {adjusting && (
            <div className="space-y-3 rounded-lg border border-subtle bg-sunken p-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  label="Amount"
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  hint="Negative reduces what they owe."
                />
                <Input
                  label="Reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setAdjusting(false)}>Cancel</Button>
                <Button
                  size="sm"
                  variant="outline"
                  loading={adjustState.isLoading}
                  disabled={!amount || Number(amount) === 0 || !reason}
                  onClick={() => void submitAdjustment()}
                >
                  Post adjustment
                </Button>
              </div>
            </div>
          )}

          <Table>
            <thead>
              <tr>
                <Th>Date</Th>
                <Th>Entry</Th>
                <Th>Reference</Th>
                <Th numeric>Charged</Th>
                <Th numeric>Paid</Th>
                <Th numeric>Balance</Th>
              </tr>
            </thead>
            <tbody className={statement.isFetching ? 'opacity-60' : undefined}>
              {statement.data?.items.map((entry) => (
                <tr key={entry.id}>
                  <Td className="text-secondary">{formatDate(entry.occurredAt, format)}</Td>
                  <Td>
                    <Badge tone={ENTRY_TONE[entry.entryType] ?? 'neutral'}>
                      {entry.entryType.toLowerCase().replace(/_/g, ' ')}
                    </Badge>
                  </Td>
                  <Td className="text-secondary">
                    {entry.referenceNumber ?? entry.description ?? '—'}
                  </Td>
                  <Td numeric className="text-secondary">
                    {Number(entry.debit) > 0 ? formatMoney(entry.debit, format) : '—'}
                  </Td>
                  <Td numeric className="text-secondary">
                    {Number(entry.credit) > 0 ? formatMoney(entry.credit, format) : '—'}
                  </Td>
                  <Td numeric className="font-medium">
                    {formatMoney(entry.balanceAfter, format)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          {statement.data && statement.data.items.length === 0 && (
            <p className="text-sm text-muted">
              Nothing on the account yet. Sales, payments and adjustments appear here.
            </p>
          )}

          {statement.data && (
            <Pagination
              page={statement.data.meta.page}
              totalPages={statement.data.meta.totalPages}
              total={statement.data.meta.total}
              onPageChange={setPage}
            />
          )}
          <ConfirmDialog
            open={archiving}
            onClose={() => setArchiving(false)}
            onConfirm={() => void confirmArchive()}
            title="Archive this customer?"
            message={`${customer.name} will be archived. A customer who still owes money cannot be archived.`}
            confirmLabel="Archive"
          />
        </div>
      ) : null}
    </Modal>
  );
}
